import { flexRender, type HeaderGroup } from '@tanstack/react-table';
import type { CSSProperties } from 'react';
import type { PinnedColumnInfo } from '../helpers/types';
import { getPinnedClassName, mergeColumnStyles } from '../helpers/utils';
import { TableHead } from './TableHead';
import { TableRow } from './TableRow';

type TableHeaderRowsProps<TData> = {
  headerGroups: HeaderGroup<TData>[];
  columnStyles: Record<string, CSSProperties | undefined>;
  weightedLeafWidthStyles: Record<string, CSSProperties>;
  pinnedColumnStyles?: Record<string, PinnedColumnInfo>;
  className?: string;
};

/**
 * テーブルヘッダー行をレンダリングするコンポーネント
 */
export const TableHeaderRows = <TData,>({
  headerGroups,
  columnStyles,
  weightedLeafWidthStyles,
  pinnedColumnStyles,
  className,
}: TableHeaderRowsProps<TData>) => {
  return (
    <>
      {headerGroups.map((headerGroup, groupIndex) => {
        // カラムグループ化されている場合、2行目以降で全てのヘッダーが空なら非表示
        if (groupIndex > 0) {
          const hasContent = headerGroup.headers.some((header) => {
            const headerContent = header.column.columnDef.header;
            return headerContent !== '' && headerContent !== undefined && headerContent !== null;
          });
          if (!hasContent) return null;
        }

        return (
          <TableRow key={headerGroup.id} className={className}>
            {headerGroup.headers.map((header) => {
              const style = columnStyles[header.column.id];
              const weightedStyle = weightedLeafWidthStyles[header.column.id];
              const mergedStyle = mergeColumnStyles(style, weightedStyle);
              const pinned = pinnedColumnStyles?.[header.column.id];
              const finalStyle = pinned ? { ...mergedStyle, ...pinned.style } : mergedStyle;
              const pinnedDataAttrs = pinned
                ? pinned.side === 'left'
                  ? { 'data-pinned-left': pinned.pinnedOffset }
                  : { 'data-pinned-right': pinned.pinnedOffset }
                : {};
              return (
                <TableHead
                  key={header.id}
                  style={finalStyle}
                  colSpan={header.colSpan}
                  className={getPinnedClassName({ pinned })}
                  {...pinnedDataAttrs}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              );
            })}
          </TableRow>
        );
      })}
    </>
  );
};
